/**
 * Client de l'API publique.
 *
 * Trois règles, et elles portent chacune un invariant :
 *
 * 1. **Le jeton d'accès vit en MÉMOIRE, jamais dans `localStorage` ni dans un cookie lisible.**
 *    En mémoire, il est hors de portée d'un XSS persistant, hors de portée d'un CSRF, et surtout
 *    hors du service worker — qui voit passer tous les appels de la même origine (invariant 17).
 *    Le prix est qu'un rechargement de page le perd : c'est le rôle du cookie de rafraîchissement
 *    de le reconstituer, et ce prix est très inférieur au risque.
 *
 * 2. **Le jeton de rafraîchissement n'est jamais touché par ce fichier.** Il vit dans le cookie
 *    `__Host-coloc_rt`, `HttpOnly` : le JavaScript ne peut pas le lire, ce qui est exactement
 *    l'objectif. `credentials: 'include'` suffit à le faire voyager (invariant 16).
 *
 * 3. **Les messages d'erreur ne viennent pas du serveur.** L'API renvoie un CODE stable dans
 *    `extensions.code` ; le rendu français / arabe RTL est fait ici (invariant 11). Afficher le
 *    `detail` du serveur produirait une interface à moitié traduite.
 */

import type { Langue } from './i18n'

const BASE = '/api/v1'

/**
 * Refuse un chemin qui redouble le préfixe de version.
 *
 * `BASE` porte déjà `/api/v1` : un chemin qui recommence par `/v1/` produit
 * `/api/v1/v1/...`, donc un 404 que l'interface affiche en « Une erreur est survenue ».
 *
 * **Ce n'est pas une précaution théorique.** Vingt-neuf appels étaient écrits ainsi — tout
 * `apiBail.ts` des deux fronts, et l'écran « Recherche » du back-office. Les écrans concernés
 * — Mes baux, Échéancier, Quittances, Modèles de bail, Relances de loyer — n'ont donc **jamais**
 * fonctionné, et personne ne s'en est aperçu parce qu'aucun bail n'existait pour les ouvrir.
 *
 * Le contrôle est ici plutôt que dans une revue : il coûte une comparaison de chaîne et
 * transforme la faute en erreur immédiate, à l'appel, avec le chemin fautif sous les yeux.
 */
function verifierChemin(chemin: string): void {
  if (chemin.startsWith('/v1/') || chemin === '/v1') {
    throw new Error(
      `Chemin d'API invalide : « ${chemin} ». BASE vaut déjà « ${BASE} » — retirer le « /v1 ».`,
    )
  }
}

let jetonAcces: string | null = null

export type Role = 'Seeker' | 'Owner' | 'Agency' | 'Tenant'
export type Genre = 'Unspecified' | 'Female' | 'Male'

export interface Profil {
  id: string
  email: string | null
  phoneNumber: string | null
  emailConfirmed: boolean
  phoneVerified: boolean
  displayName: string
  firstName: string | null
  lastName: string | null
  bio: string | null
  birthDate: string | null
  gender: Genre | null
  preferredLanguage: Langue | 'en'
  /** La ville du compte (D45) — ce que l'application retrouve sur un autre appareil. Nul = jamais choisie. */
  preferredCityCode: string | null
  status: string
  trustLevel: 'N0' | 'N1' | 'N2' | 'N3' | 'N4'
  profileComplete: boolean
  memberSince: string
  activeRoles: Role[]
}

/** Échec d'appel porteur du code stable de l'API. Le rendu est la responsabilité de l'appelant. */
export class ErreurApi extends Error {
  constructor(
    readonly code: string,
    readonly statut: number,
    readonly champs?: Record<string, string[]>,
    /** Secondes annoncées par `Retry-After`. Une limitation de débit se dit, elle ne se subit pas. */
    readonly reessayerDans?: number,
  ) {
    super(code)
    this.name = 'ErreurApi'
  }
}

interface ProblemDetails {
  code?: string
  errors?: Record<string, string[]>
}

export function jetonCourant(): string | null {
  return jetonAcces
}

export async function lireErreur(reponse: Response): Promise<ErreurApi> {
  let corps: ProblemDetails = {}

  try {
    corps = (await reponse.json()) as ProblemDetails
  } catch {
    // Une réponse sans corps JSON reste une erreur exploitable : le statut suffit.
  }

  /*
   * Un 429 n'est pas une panne, et l'écran ne doit pas le dire comme telle.
   *
   * Le limiteur ne renvoie pas toujours de code applicatif : sans ce repli, « Trop de tentatives »
   * s'affichait « Une erreur est survenue. Réessayez. » — un message qui invite à refaire
   * exactement ce que le serveur vient de refuser. Trouvé en pilotant l'application pendant
   * l'audit de L10c, sur un limiteur qui faisait son travail.
   */
  const code = corps.code ?? (reponse.status === 429 ? 'rate_limited' : 'internal')
  const delai = Number(reponse.headers.get('Retry-After') ?? '')

  return new ErreurApi(
    code,
    reponse.status,
    corps.errors,
    Number.isFinite(delai) && delai > 0 ? delai : undefined,
  )
}

/**
 * Appel authentifié, avec UN seul réessai après rafraîchissement.
 *
 * Un seul, et pas une boucle : si le rafraîchissement échoue à son tour, la session est finie et
 * insister ne ferait que multiplier les appels au moment précis où le serveur dit non.
 */
export async function appeler<T>(
  chemin: string,
  options: RequestInit = {},
  reessayer = true,
): Promise<T> {
  const entetes = new Headers(options.headers)
  entetes.set('Accept', 'application/json')

  if (options.body !== undefined) {
    entetes.set('Content-Type', 'application/json')
  }

  if (jetonAcces) {
    entetes.set('Authorization', `Bearer ${jetonAcces}`)
  }

  verifierChemin(chemin)

  const reponse = await fetch(`${BASE}${chemin}`, {
    ...options,
    headers: entetes,
    // Indispensable : sans cela le cookie __Host-coloc_rt ne part pas, et le rafraîchissement
    // échoue systématiquement avec un message qui n'aide personne.
    credentials: 'include',
  })

  if (reponse.status === 401 && reessayer && jetonAcces !== null) {
    if (await rafraichir()) {
      return appeler<T>(chemin, options, false)
    }
  }

  if (!reponse.ok) {
    throw await lireErreur(reponse)
  }

  // 204, et 202 sans corps : `POST /leases/{id}/model` accepte sans rien renvoyer, et un
  // `json()` sur un corps vide lève — le modèle de bail s'engendrait bien, l'écran disait une erreur.
  const type = reponse.headers.get('content-type') ?? ''

  if (reponse.status === 204 || (reponse.status === 202 && !type.includes('json'))) {
    return undefined as T
  }

  return (await reponse.json()) as T
}

/**
 * Télécharge un document de l'API et déclenche l'enregistrement du fichier.
 *
 * **Un `fetch` authentifié puis un objet URL, jamais un `<a href>` nu sur `/api/*`.** Le jeton
 * d'accès vit en mémoire (règle 1) : un lien direct part sans `Authorization`, et le serveur
 * répond 401. C'était le cas des quittances et du modèle de bail depuis L6 — l'export de données
 * (L9) avait déjà son propre `fetch`, celui-ci le généralise. Un seul réessai après
 * rafraîchissement, comme `appeler`.
 *
 * Le nom du fichier est celui que le serveur donne (`Content-Disposition`) ; `nomParDefaut` ne
 * sert que s'il n'en donne pas.
 */
export async function telecharger(chemin: string, nomParDefaut: string, reessayer = true): Promise<void> {
  const entetes = new Headers()

  if (jetonAcces) {
    entetes.set('Authorization', `Bearer ${jetonAcces}`)
  }

  verifierChemin(chemin)

  const reponse = await fetch(`${BASE}${chemin}`, { headers: entetes, credentials: 'include' })

  if (reponse.status === 401 && reessayer && jetonAcces !== null) {
    if (await rafraichir()) {
      return telecharger(chemin, nomParDefaut, false)
    }
  }

  if (!reponse.ok) {
    throw await lireErreur(reponse)
  }

  const disposition = reponse.headers.get('Content-Disposition') ?? ''
  const nomServeur = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1] ?? /filename="?([^";]+)"?/i.exec(disposition)?.[1]
  const blob = await reponse.blob()
  const url = URL.createObjectURL(blob)

  const lien = document.createElement('a')
  lien.href = url
  lien.download = nomServeur === undefined ? nomParDefaut : decodeURIComponent(nomServeur)
  lien.click()

  // Sans révocation, le blob reste en mémoire jusqu'au rechargement de la page.
  URL.revokeObjectURL(url)
}

/**
 * Lecture CONDITIONNELLE : renvoie `null` quand rien n'a change - L7.
 *
 * C'est la moitie cliente du contrat de fraicheur de D29 (conception L7, 1.1 et 3.4). Le serveur
 * repond `ETag`, on le renvoie en `If-None-Match`, et une liste inchangee revient en `304` :
 * quelques centaines d'octets au lieu du chargement complet. Sans cela, interroger toutes les
 * quatre secondes serait exactement ce qu'on reproche au polling.
 *
 * L'appelant recoit `null` pour « inchange », ce qui lui evite de comparer quoi que ce soit.
 */
export async function lireSiModifie<T>(
  chemin: string,
  versionConnue: number | null,
  reessayer = true,
): Promise<T | null> {
  const entetes = new Headers()
  entetes.set('Accept', 'application/json')

  if (versionConnue !== null) {
    entetes.set('If-None-Match', `"${versionConnue}"`)
  }

  if (jetonAcces) {
    entetes.set('Authorization', `Bearer ${jetonAcces}`)
  }

  verifierChemin(chemin)

  const reponse = await fetch(`${BASE}${chemin}`, { headers: entetes, credentials: 'include' })

  if (reponse.status === 401 && reessayer && jetonAcces !== null) {
    if (await rafraichir()) {
      return lireSiModifie<T>(chemin, versionConnue, false)
    }
  }

  if (reponse.status === 304) {
    return null
  }

  if (!reponse.ok) {
    throw await lireErreur(reponse)
  }

  return (await reponse.json()) as T
}

export async function inscrire(
  contact: string,
  motDePasse: string,
  nomAffiche: string,
  langue: Langue,
): Promise<void> {
  await appeler<{ userId: string }>('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ contact, password: motDePasse, displayName: nomAffiche, language: langue }),
  })
}

export async function connecter(contact: string, motDePasse: string): Promise<void> {
  const session = await appeler<{ accessToken: string }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ contact, password: motDePasse }),
  })

  jetonAcces = session.accessToken
}

/**
 * Reconstitue une session depuis le cookie de rafraîchissement.
 *
 * Appelé au démarrage de l'application : c'est ce qui rend acceptable de garder le jeton d'accès
 * en mémoire seulement. Un échec est le cas nominal d'un visiteur non connecté, pas un incident —
 * d'où le booléen plutôt qu'une exception.
 */
export async function rafraichir(): Promise<boolean> {
  const reponse = await fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })

  if (!reponse.ok) {
    jetonAcces = null

    // Un 5xx — ou le 502 du frontal quand l'API est absente — n'est pas « pas connecté » : c'est
    // une panne, et l'application doit la montrer plutôt que de démarrer en visiteur anonyme
    // devant des écrans qui échoueront tous. Le réseau coupé lève déjà, par `fetch` lui-même.
    if (reponse.status >= 500) {
      throw await lireErreur(reponse)
    }

    return false
  }

  const session = (await reponse.json()) as { accessToken: string }
  jetonAcces = session.accessToken

  return true
}

/**
 * Ferme la session et **purge le cache du service worker** (invariant 17).
 *
 * La PWA et l'API publique partagent une origine : le service worker voit passer tous les appels.
 * `/api/*` est en `NetworkOnly`, donc rien d'authentifié ne devrait s'y trouver — mais une purge
 * à la déconnexion est ce qui garantit qu'un changement de compte sur un téléphone partagé ne
 * ressert jamais l'écran du précédent.
 */
export async function deconnecter(): Promise<void> {
  try {
    await fetch(`${BASE}/auth/logout`, { method: 'POST', credentials: 'include' })
  } finally {
    jetonAcces = null
    await purgerCaches()
  }
}

export async function deconnecterPartout(): Promise<void> {
  try {
    await appeler<void>('/auth/logout-all', { method: 'POST' })
  } finally {
    jetonAcces = null
    await purgerCaches()
  }
}

async function purgerCaches(): Promise<void> {
  if (!('caches' in globalThis)) {
    return
  }

  const noms = await caches.keys()
  await Promise.all(noms.map((nom) => caches.delete(nom)))
}

export function profil(): Promise<Profil> {
  return appeler<Profil>('/me')
}

export interface MiseAJourProfil {
  displayName?: string | null
  firstName?: string | null
  lastName?: string | null
  bio?: string | null
  birthDate?: string | null
  gender?: Genre | null
  preferredLanguage?: string | null
  preferredCityCode?: string | null
}

export function mettreAJourProfil(mise: MiseAJourProfil): Promise<void> {
  return appeler<void>('/me', { method: 'PATCH', body: JSON.stringify(mise) })
}

export function activerRole(role: Role): Promise<void> {
  return appeler<void>(`/me/roles/${role.toLowerCase()}`, { method: 'POST' })
}

export function desactiverRole(role: Role): Promise<void> {
  return appeler<void>(`/me/roles/${role.toLowerCase()}`, { method: 'DELETE' })
}

/**
 * Demande de réinitialisation.
 *
 * La réponse est volontairement identique que le compte existe ou non — corps et durée. Ne jamais
 * « améliorer » l'écran en affichant « compte introuvable » : ce serait rendre au serveur, côté
 * client, l'oracle d'énumération qu'il refuse de donner.
 */
export function demanderReinitialisation(contact: string): Promise<void> {
  return appeler<void>('/auth/password/forgot', {
    method: 'POST',
    body: JSON.stringify({ contact }),
  })
}

export function reinitialiserMotDePasse(token: string, nouveauMotDePasse: string): Promise<void> {
  return appeler<void>('/auth/password/reset', {
    method: 'POST',
    body: JSON.stringify({ token, newPassword: nouveauMotDePasse }),
  })
}
